import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Building2, Download, Search, Wrench } from "lucide-react";
import { api, apiError, API, fmtINR } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const FIELDS = [
  ["name", "Company Name"], ["gstin", "GSTIN"], ["pan", "PAN"],
  ["address", "Registered Address"], ["city", "City"], ["state", "State"], ["state_code", "State Code"], ["pincode", "Pincode"],
  ["mobile", "Mobile"], ["phone", "Phone"], ["email", "Email"], ["website", "Website"],
  ["bank_name", "Bank Name"], ["account_number", "Account Number"], ["ifsc", "IFSC"],
  ["branch", "Branch"], ["upi_id", "UPI ID"], ["authorized_signatory", "Authorized Signatory"],
  ["invoice_prefix", "Invoice No Prefix (e.g. AMPL/2026-27/)"],
  ["pl_report_emails", "Monthly P&L Email Recipients (CA email, comma separated)"],
  ["default_labour_cost", "Default Labour Charge on Invoice (₹)"],
  ["default_transport_cost", "Default Transport Charge on Invoice (₹)"],
  ["company_whatsapp", "Company WhatsApp Number (with country code, e.g. 919825012345)"],
  ["po_prefix", "Purchase Order No Prefix (e.g. AMPL/PO/2026-27/)"],
  ["quotation_prefix", "Quotation No Prefix (e.g. AMPL/QT/2026-27/)"],
  ["bank_opening_balance", "Bank Opening Balance (₹)"],
  ["cash_opening_balance", "Cash Opening Balance (₹)"],
  ["low_balance_limit", "Low Balance Alert Limit (₹)"],
];

export default function CompanySettings() {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [rec, setRec] = useState(null);
  const [recBusy, setRecBusy] = useState(false);

  useEffect(() => { api.get("/company").then((r) => setForm(r.data || {})); }, []);

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500000) { toast.error("Logo must be under 500KB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, logo: reader.result }));
    reader.readAsDataURL(file);
  };

  const onBankQr = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(png|jpe?g)$/i.test(file.type)) { toast.error("Only PNG/JPG allowed"); return; }
    if (file.size > 800000) { toast.error("QR must be under 800KB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, bank_qr: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try { await api.put("/company", form); toast.success("Company settings saved"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const downloadBackup = () => {
    window.open(`${API}/backup/xlsx?token=${localStorage.getItem("token")}`, "_blank");
  };

  const runReconcile = async (dry) => {
    if (!dry && !window.confirm("Create the missing payment entries? They will be recorded as Bank receipts/payments dated as per each document.")) return;
    setRecBusy(true);
    try {
      const r = await api.post(`/admin/reconcile-payments?dry_run=${dry}&payment_mode=Bank%20Transfer`);
      setRec(r.data);
      if (!dry) toast.success(`${r.data.count} payment entr${r.data.count === 1 ? "y" : "ies"} created`);
      else if (r.data.count === 0) toast.success("No missing payment entries found");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setRecBusy(false);
  };

  return (
    <div data-testid="company-settings">
      <div className="mb-6"><h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-head">Company Settings</h1>
        <p className="text-sm text-slate-500 mt-0.5">Used dynamically across all PDF documents</p></div>

      <div className="bg-white border border-slate-200 rounded-sm p-5 sm:p-6 max-w-4xl">
        <div className="flex items-center gap-4 mb-6 pb-6 border-b border-slate-100">
          <div className="w-20 h-20 rounded-sm border border-slate-200 flex items-center justify-center overflow-hidden bg-slate-50">
            {form.logo ? <img src={form.logo} alt="logo" className="w-full h-full object-contain" /> : <Building2 className="w-8 h-8 text-slate-300" />}
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Company Logo</label>
            <input data-testid="company-logo" type="file" accept="image/*" onChange={onLogo} className="block mt-1.5 text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-sm file:border-0 file:bg-[#0B132B] file:text-white file:text-xs file:cursor-pointer" />
            <p className="text-xs text-slate-400 mt-1">PNG/JPG, under 500KB. Appears on PDFs.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FIELDS.map(([key, label]) => (
            <div key={key} className="space-y-1.5"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
              <Input data-testid={`company-${key}`} value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className="bg-white" /></div>
          ))}
        </div>
        <div className="mt-4 space-y-1.5"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Terms &amp; Conditions</label>
          <Textarea data-testid="company-terms" value={form.terms || ""} onChange={(e) => setForm({ ...form, terms: e.target.value })} className="bg-white" rows={3} /></div>

        <div className="mt-6 flex items-start gap-4" data-testid="bank-qr-section">
          <div className="w-24 h-24 rounded-sm border border-slate-200 flex items-center justify-center overflow-hidden bg-slate-50">
            {form.bank_qr ? <img src={form.bank_qr} alt="bank qr" className="w-full h-full object-contain" data-testid="bank-qr-preview" /> : <span className="text-[10px] text-slate-300 text-center px-2">No QR</span>}
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Bank QR Code</label>
            <input data-testid="bank-qr-input" type="file" accept=".jpg,.jpeg,.png,image/png,image/jpeg" onChange={onBankQr} className="block mt-1.5 text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-sm file:border-0 file:bg-[#0B132B] file:text-white file:text-xs file:cursor-pointer" />
            <p className="text-xs text-slate-400 mt-1">JPG/PNG, under 800KB. Appears on Quotation &amp; Invoice PDFs near Bank Details.</p>
            {form.bank_qr && <button type="button" data-testid="bank-qr-remove" onClick={() => setForm((f) => ({ ...f, bank_qr: null }))} className="text-xs text-red-600 hover:underline mt-1">Remove QR Code</button>}
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-slate-100" data-testid="backup-section">
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Data Backup</label>
          <p className="text-xs text-slate-400 mt-1 mb-2">Download a full snapshot of the database as a multi-sheet Excel file (one sheet per module). Admin only.</p>
          <Button data-testid="download-backup-btn" variant="outline" onClick={downloadBackup} className="rounded-sm gap-1.5"><Download className="w-4 h-4" /> Download All Data (Excel)</Button>
        </div>

        <div className="mt-6 pt-6 border-t border-slate-100" data-testid="reconcile-section">
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Fix Missing Payment Entries</label>
          <p className="text-xs text-slate-400 mt-1 mb-2">Finds invoices &amp; bills where a Paid Amount was recorded without a payment entry, so the money never reached the Company Bank Balance. Preview first, then apply. Admin only.</p>
          <div className="flex items-center gap-2 flex-wrap">
            <Button data-testid="reconcile-preview-btn" variant="outline" onClick={() => runReconcile(true)} disabled={recBusy} className="rounded-sm gap-1.5"><Search className="w-4 h-4" /> Preview Missing Entries</Button>
            {rec && rec.count > 0 && (
              <Button data-testid="reconcile-apply-btn" onClick={() => runReconcile(false)} disabled={recBusy} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm gap-1.5"><Wrench className="w-4 h-4" /> Create {rec.count} Payment Entr{rec.count === 1 ? "y" : "ies"} (Bank)</Button>
            )}
          </div>
          {rec && (
            <div className="mt-3 border border-slate-200 rounded-sm overflow-x-auto" data-testid="reconcile-result">
              <div className="px-3 py-2 bg-slate-50 text-sm text-slate-700 border-b border-slate-200">
                {rec.count === 0 ? "Everything is in sync — no missing payment entries." :
                  `${rec.count} document(s) · ${fmtINR(rec.total_missing)} ${rec.dry_run ? "not yet in the bank balance" : "added to the bank balance"}`}
              </div>
              {rec.count > 0 && (
                <table className="w-full text-sm">
                  <thead><tr className="bg-white border-b border-slate-200 text-left">
                    {["Type", "Doc No", "Date", "Party", "Paid", "Already Recorded", "Missing"].map((h, i) => (
                      <th key={h} className={`px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 ${i >= 4 ? "text-right" : ""}`}>{h}</th>))}
                  </tr></thead>
                  <tbody>
                    {rec.items.map((it, i) => (
                      <tr key={i} className="border-b border-slate-100">
                        <td className="px-3 py-2 capitalize text-slate-600">{it.type}</td>
                        <td className="px-3 py-2 text-slate-800">{it.doc_no}</td>
                        <td className="px-3 py-2 text-slate-600">{it.date}</td>
                        <td className="px-3 py-2 text-slate-700">{it.party_name}</td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtINR(it.paid_amount)}</td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-500">{fmtINR(it.already_recorded)}</td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold text-red-600">{fmtINR(it.missing_amount)}</td>
                      </tr>))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        <div className="mt-6"><Button data-testid="save-company-btn" onClick={save} disabled={saving} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm gap-1.5"><Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Settings"}</Button></div>
      </div>
    </div>
  );
}
